import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { Panel } from '@hologram-dimension/panel';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { TextareaField } from '@hologram-dimension/textarea-field';
import { getOrganizationsByIds, searchOrganizations } from 'common-js/api';
import useAppDispatch from 'common-js/hooks/useAppDispatch';
import {
  makeReleaseFlagPublic,
  removeReleaseFlagFromPublic,
  setReleaseFlagForOrg,
  setReleaseFlagForOrgs,
} from 'common-js/reducers/admin/actions';
import parseInt from 'lodash/parseInt';
import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

interface OrgRowProps {
  id: OrgId;
  name: string;
  flag: string;
  add?: boolean;
}

const OrgRow: FC<OrgRowProps> = ({ id, name, flag, add = false }) => {
  const dispatch = useAppDispatch();

  const setForOrg = useCallback(() => {
    dispatch(setReleaseFlagForOrg(id, flag, add ? 'on' : 'off'));
  }, [add, dispatch, flag, id]);

  return (
    <li className="AdminFlagPanel__organization-item">
      {name} (id: {id}){' '}
      <Button iconStart={add ? 'Plus' : 'Close'} onClick={setForOrg} variant="tertiary" />
    </li>
  );
};

const MAX_CHUNK = 10000 as const;
const MAX_REQUESTS = 5 as const;

async function* resultGenerator(
  releaseFlag: string,
  requestChunks: Array<Array<Array<OrgId>>>,
  add: boolean,
  dispatch: any
) {
  let i = 0;

  while (i < requestChunks.length) {
    const requests = requestChunks[i].map(async (chunk) => {
      try {
        const result = await dispatch(
          setReleaseFlagForOrgs(chunk, releaseFlag, add ? 'on' : 'off')
        );

        return result;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to update organization ID ${chunk}: ${error}`);
        return error;
      }
    });

    yield Promise.all(requests);
    i += 1;
  }
}

interface AdminFlagPanelSearchProps {
  flag: string;
}

const AdminFlagPanelSearch: FC<AdminFlagPanelSearchProps> = ({ flag }) => {
  const timeoutIdRef = useRef<number>();

  const [searchResults, setSearchResults] = useState<Array<any>>([]);
  const [parsedOrgs, setParsedOrgs] = useState<Set<OrgId>>(new Set());
  const [loadingSearchResults, setLoadingSearchResults] = useState(false);
  const [bulkFlagging, setBulkFlagging] = useState(false);

  const orgSearch = useCallback(
    (query) => {
      setLoadingSearchResults(true);

      if (!query || query === '') {
        setSearchResults([]);
        setLoadingSearchResults(false);
        return;
      }

      const orgId = parseInt(query);
      if (!Number.isNaN(orgId)) {
        getOrganizationsByIds([orgId])
          .then((result) => {
            const { orgs } = result;
            setSearchResults(orgs);
            setLoadingSearchResults(false);
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
          });

        return;
      }

      const encoded = encodeURIComponent(query);
      searchOrganizations(encoded, false)
        .then((result) => {
          const { orgs } = result;
          setSearchResults(orgs);
          setLoadingSearchResults(false);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        });
    },
    [setSearchResults, setLoadingSearchResults]
  );

  const onOrgSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const query = e.target.value;
      // Debounce so we don't make too many API calls
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
      }
      timeoutIdRef.current = window.setTimeout(() => {
        orgSearch(query);
      }, 300);
    },
    [orgSearch]
  );

  const onOrgCsvChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    const query = e.target.value;

    setParsedOrgs(
      new Set(
        query
          .split(',')
          .map((org) => (org ? parseInt(org.trim(), 10) : null))
          .filter((org) => !!org) as any
      )
    );
  }, []);

  const orgFlagChunks = useMemo(() => {
    const chunks = [...parsedOrgs.values()].reduce<Array<Array<OrgId>>>(
      (resultArray, item, index) => {
        const chunkIndex = Math.floor(index / MAX_CHUNK);

        if (!resultArray[chunkIndex]) {
          // eslint-disable-next-line no-param-reassign
          resultArray[chunkIndex] = [];
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
      },
      []
    );

    return chunks.reduce<Array<Array<Array<OrgId>>>>((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / MAX_REQUESTS);

      if (!resultArray[chunkIndex]) {
        // eslint-disable-next-line no-param-reassign
        resultArray[chunkIndex] = [];
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);
  }, [parsedOrgs]);

  const dispatch = useAppDispatch();
  const csvOrgFlagRef = useRef<HTMLTextAreaElement>(null);
  const flagOrgs = useCallback(
    async (add: boolean) => {
      setBulkFlagging(true);

      let results: Array<any> = [];
      // eslint-disable-next-line no-restricted-syntax
      for await (const resultChunk of resultGenerator(flag, orgFlagChunks, add, dispatch)) {
        results = results.concat(resultChunk);
      }

      setBulkFlagging(false);
      csvOrgFlagRef.current!.value = '';
      setParsedOrgs(new Set());
      return results;
    },
    [dispatch, flag, orgFlagChunks]
  );

  const preventSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault();
  }, []);

  return (
    <>
      <Form onSubmit={preventSubmit}>
        <TextInputField
          id={`${flag}-search`}
          label="Add flag to organization"
          onChange={onOrgSearchChange}
        />
        <TextareaField
          id={`${flag}-multi-org`}
          label="Modify flag for multiple organizations (comma-separated)"
          onChange={onOrgCsvChange}
          ref={csvOrgFlagRef}
        />
        <div
          style={{
            display: 'flex',
            flexFlow: 'row nowrap',
            gap: '8px',
            justifyContent: 'right',
            width: '100%',
          }}
        >
          <Button
            disabled={parsedOrgs.size === 0}
            onClick={() => flagOrgs(false)}
            loading={bulkFlagging}
            variant="delete"
          >
            Remove
          </Button>
          <Button
            disabled={parsedOrgs.size === 0}
            onClick={() => flagOrgs(true)}
            loading={bulkFlagging}
            variant="secondary"
          >
            Add
          </Button>
        </div>
      </Form>
      {loadingSearchResults && 'Loading...'}
      <ul className="AdminFlagPanel__organization-list">
        {searchResults.map((r) => (
          <OrgRow key={r.id} add id={r.id} name={r.name} flag={flag} />
        ))}
      </ul>
    </>
  );
};

interface AdminFlagPanelProps {
  flag: string;
  isPublic: boolean;
  activeOrganizations: Array<any>;
}

const AdminFlagPanel: FC<AdminFlagPanelProps> = ({ flag, isPublic, activeOrganizations = [] }) => {
  const dispatch = useAppDispatch();

  const makePublic = useCallback(() => {
    dispatch(makeReleaseFlagPublic(flag));
  }, [dispatch, flag]);

  const removeFromPublic = useCallback(() => {
    dispatch(removeReleaseFlagFromPublic(flag));
  }, [dispatch, flag]);

  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const [queryText, setQueryText] = useState('');

  const onFilterTextChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    const query = e.target.value;

    setQueryText(query.toLocaleLowerCase());
  }, []);
  const activeOrgCount = activeOrganizations?.length ?? 0;
  const invalidSearch = queryText.length < 3 && queryText.length > 0;
  const queryId = queryText.length > 0 ? parseInt(queryText, 10) : null;

  const preventSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault();
  }, []);

  const onCopyCsv = useCallback(() => {
    const orgIdsToCsv = activeOrganizations
      .filter(
        (org) =>
          (queryId !== null && queryId === org.id) ||
          org.name.toLocaleLowerCase().includes(queryText)
      )
      .map((org) => org.id)
      .join(',');
    navigator.clipboard.writeText(orgIdsToCsv);
  }, [activeOrganizations, queryId, queryText]);

  return (
    <Panel
      className="AdminFlagPanel"
      header={flag}
      headerActions={
        <div className="AdminFlagPanel__headerActions">
          {isPublic ? (
            <>
              <b>Active for all users</b>
              <Button onClick={removeFromPublic} variant="secondary">
                Restrict to specific orgs
              </Button>
            </>
          ) : (
            <>
              <span>Only for specific organizations</span>
              <Button onClick={makePublic} variant="secondary">
                Activate for all users
              </Button>
            </>
          )}
        </div>
      }
    >
      {!isPublic && (
        <div className="AdminFlagPanel__body">
          <div className="AdminFlagPanel__search">
            <AdminFlagPanelSearch flag={flag} />
          </div>
          <div className="AdminFlagPanel__organizations">
            <div className="AdminFlagPanel__organizations-header">
              <h2>Organizations with flag ({activeOrgCount})</h2>
              <Button
                iconStart="Document"
                variant="secondary"
                onClick={onCopyCsv}
                title="Copy organization IDs as CSV"
              />
              <Button variant="secondary" onClick={toggleExpanded}>
                {expanded ? 'Collapse' : 'Expand'}
              </Button>
            </div>
            {activeOrgCount > 50 && (
              <Form onSubmit={preventSubmit}>
                <TextInputField
                  id={`${flag}-org-list-search`}
                  label=""
                  onChange={onFilterTextChange}
                  defaultValue={queryText}
                  placeholder="Filter organizations"
                  validationMessage={
                    invalidSearch ? 'Need at least three characters to filter' : ''
                  }
                  invalid={invalidSearch}
                />
              </Form>
            )}
            {(expanded || queryText.length >= 3) && (
              <ul className="AdminFlagPanel__organization-list AdminFlagPanel__existing-org-list">
                {queryText.length > 0
                  ? activeOrganizations
                      .filter(
                        (org) =>
                          (queryId !== null && queryId === org.id) ||
                          org.name.toLocaleLowerCase().includes(queryText)
                      )
                      .map((org) => (
                        <OrgRow key={flag + org.id} id={org.id} name={org.name} flag={flag} />
                      ))
                  : activeOrganizations.map((org) => (
                      <OrgRow key={flag + org.id} id={org.id} name={org.name} flag={flag} />
                    ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
};

export default AdminFlagPanel;
